/* eslint-disable @typescript-eslint/no-explicit-any */
import AccountSection from './AccountSection';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  useAmazonAccountGet,
  useAmazonAccountPost,
  useAmazonAccountRemove,
} from '@/core/domain/enterprise/AmazonAccount/amazonAccount.hooks';
import { Alert, Card, Modal, Popover, message, notification } from 'antd';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import AlexaInfo from './AlexaInfo';
import LoginWithAmazon from './lwa';
import { Group } from '@/components/_common';
import { FaQuestionCircle } from 'react-icons/fa';

const lwa = new LoginWithAmazon(process.env.REACT_APP_LWA_CLIENT_ID || '');

const clearSearch = () => {
  const url = new URL(window.location as any);
  url.search = '';
  history.replaceState(null, '', url.toString());
};

export default function LinkinAccountAmazon() {
  const history = useHistory();

  const amazonAccountPost = useAmazonAccountPost();
  const amazonAccountGet = useAmazonAccountGet();
  const amazonAccountRemove = useAmazonAccountRemove();

  const hasLinkAccount = !!amazonAccountGet.data?.id;

  const [loading, setLoading] = useState(false);

  const retrieveSearchCode = useCallback(async () => {
    try {
      const authorizeCode = await lwa.retriveCode();

      if (authorizeCode.code) {
        amazonRetrieveProfile(authorizeCode.code);
      }
    } catch (error: any) {
      console.error(error);
      notification.error({
        message: 'Login da Amazon',
        description: LoginWithAmazon.Errors[error.message] || error.message,
      });
      clearSearch();
    }
  }, [history]);

  useEffect(() => {
    retrieveSearchCode();
  }, [retrieveSearchCode]);

  const amazonRetrieveProfile = async (code: string) => {
    try {
      setLoading(true);

      const amazonToken = await lwa.retriveAccessToken(code);

      const amazonProfile = await lwa.retriveProfile(amazonToken.access_token);

      await amazonAccountPost.mutateAsync({
        email: amazonProfile.email,
        userId: amazonProfile.user_id,
      });
    } catch (error: any) {
      console.error(error);
      notification.error({
        message: 'Login da Amazon',
        description: LoginWithAmazon.Errors[error.message] || error.message,
      });
      clearSearch();
    } finally {
      setLoading(false);
    }
  };

  const amazonLogin = useCallback(() => {
    try {
      const redirectUri = window.location.href.split('?')[0].replace('#', '');
      lwa.authorize(redirectUri);
    } catch {
      message.error(
        'Não foi possível iniciar a chamada de login com a Amazon, tente novamente.',
      );
    }
  }, []);

  const handleCheck = (checked: boolean) => {
    if (checked) {
      amazonLogin();
    } else {
      Modal.confirm({
        width: 600,
        title: 'Desvincular conta Amazon',
        content: (
          <>
            <p>
              Você está prestes a remover o vinculo da conta da Amazon ao seu
              perfil da plataforma.
            </p>

            <Alert
              type="warning"
              description="Ao desvincular você não terá mais acesso as funcionalidades da Ultracar na Alexa"
            />
          </>
        ),
        okText: 'Desvincular conta',
        okType: 'danger',
        onOk: () => amazonAccountRemove.mutateAsync(),
      });
    }
  };

  return (
    <>
      {loading && (
        <ModalLoadingOverlay messages={['Vinculando conta da amazon...']} />
      )}

      <Card style={{ borderRadius: 8 }}>
        <AccountSection
          loading={false}
          checked={hasLinkAccount}
          onCheck={handleCheck}
          section={{
            icon: (
              <img src="/images/amazon-logo.png" alt="Amazon logo" width={70} />
            ),
            title: (
              <Group gap={6}>
                <span>Amazon</span>
                <Popover
                  content={
                    <div className="markdown">
                      Serão salvos os dados: <code>ClientId</code> e{' '}
                      <code>E-mail</code>
                    </div>
                  }
                >
                  <FaQuestionCircle fontSize={16} />
                </Popover>
              </Group>
            ),
            description:
              'Vincule sua conta Amazon ao seu perfil no Ultracar e tenha acesso às funcionalidades com os produtos da Amazon.',
          }}
        />

        <p style={{ marginTop: 24 }}>
          <strong>Aplicativos para sua oficina</strong>
        </p>

        <Card style={{ borderRadius: 8, marginTop: 24 }} type="inner">
          <AlexaInfo />
        </Card>
      </Card>
    </>
  );
}
